// EmailVerifiedPage.js - CHANGES REQUIRED

import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { auth } from "../../config/firebase";
import { applyActionCode } from "firebase/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { CheckCircle, XCircle, Loader, Flag, Gift, Mail } from "lucide-react"; // ADD: Gift, Mail icons

function EmailVerifiedPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const [welcomeEmailSent, setWelcomeEmailSent] = useState(false); // ADD: Welcome email state
  const navigate = useNavigate();

  // ADD: Function to send welcome email
  const sendWelcomeEmail = async (email) => {
    try {
      console.log(`🎉 Sending welcome email to: ${email}`);
      
      const response = await fetch(
        'https://us-central1-semaphore-app-db378.cloudfunctions.net/sendWelcomeEmail',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email: email,
            displayName: 'there' // You could extract this from URL if needed
          }),
        }
      );

      if (response.ok) {
        console.log('✅ Welcome email sent successfully');
        setWelcomeEmailSent(true);
      } else {
        console.error('❌ Welcome email failed:', response.statusText);
      }
    } catch (error) {
      console.error('❌ Welcome email error:', error);
      // Don't block user experience if welcome email fails
    }
  };

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get the verification code from URL parameters
        const oobCode = searchParams.get("oobCode") || 
                       searchParams.get("actionCode") || 
                       searchParams.get("code");
        
        const mode = searchParams.get("mode");
        
        // ADD: Get email parameter for welcome email
        const email = searchParams.get("email");
        
        console.log("🔍 Verification attempt:", { oobCode: !!oobCode, mode, email });
        console.log("📋 All URL params:", Object.fromEntries(searchParams));

        // If no verification code, assume verification already happened via Firebase
        if (!oobCode) {
          console.log("🎉 No verification code found - assuming already verified by Firebase");
          setStatus("success");
          
          // ADD: Send welcome email if we have the email parameter
          if (email) {
            await sendWelcomeEmail(email);
          }
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate("/login", { 
              state: { 
                message: "Email verified successfully! You can now log in." 
              }
            });
          }, 3000);
          return;
        }

        // Verify that this is actually an email verification action
        if (mode && mode !== "verifyEmail") {
          console.error("❌ Wrong action mode:", mode);
          setError("This link is not for email verification. Please use the correct verification link from your email.");
          setStatus("error");
          return;
        }

        console.log("🔄 Applying action code...");
        await applyActionCode(auth, oobCode);
        
        console.log("✅ Email verification successful");
        setStatus("success");
        
        // ADD: Send welcome email after successful verification
        if (email) {
          await sendWelcomeEmail(email);
        }
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login", { 
            state: { 
              message: "Email verified successfully! You can now log in." 
            }
          });
        }, 3000);

      } catch (error) {
        console.error("❌ Email verification failed:", error);
        
        // If the code was already used, that's actually success
        if (error.code === "auth/invalid-action-code") {
          console.log("🎉 Code already used - verification was successful");
          setStatus("success");
          
          // ADD: Send welcome email even if code was already used
          const email = searchParams.get("email");
          if (email) {
            await sendWelcomeEmail(email);
          }
          
          setTimeout(() => {
            navigate("/login", { 
              state: { 
                message: "Email verified successfully! You can now log in." 
              }
            });
          }, 3000);
          return;
        }
        
        let errorMessage = "Verification failed. ";
        
        switch (error.code) {
          case "auth/expired-action-code":
            errorMessage = "This verification link has expired. Please request a new verification email.";
            break;
          case "auth/user-disabled":
            errorMessage = "This user account has been disabled.";
            break;
          case "auth/user-not-found":
            errorMessage = "No user account found for this verification link.";
            break;
          default:
            errorMessage += error.message || "Please try again or contact support.";
        }
        
        setError(errorMessage);
        setStatus("error");
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div style={{ width: '500px' }}>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-2">
              <Flag className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
              {status === "verifying" && <Loader className="h-6 w-6 animate-spin" />}
              {status === "error" && <XCircle className="h-6 w-6 text-red-500" />}
              
              {status === "success" && "Email Verified!"}
              {status === "verifying" && "Verifying Email..."}
              {status === "error" && "Verification Failed"}
            </CardTitle>
            <p className="text-sm text-gray-500 text-center">Semafor Account Management</p>
          </CardHeader>
          
          <CardContent className="px-6">
            {/* Verifying State */}
            {status === "verifying" && (
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Loader className="h-8 w-8 animate-spin text-blue-500" />
                </div>
                <p className="text-gray-600">Please wait while we verify your email address...</p>
              </div>
            )}

            {/* Success State */}
            {status === "success" && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500" /> 
                    <p className="text-green-700 font-medium">Email Verified Successfully!</p>
                  </div>
                  <p className="text-green-600 text-sm">
                    Your email has been verified. You can now access all Semafor features.
                  </p>
                </div>

                {/* ADD: Welcome Email Status */}
                {searchParams.get("email") && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {welcomeEmailSent ? (
                        <>
                          <Gift className="h-5 w-5 text-blue-500" />
                          <p className="text-blue-700 font-medium">Welcome email sent! 📬</p>
                        </>
                      ) : (
                        <>
                          <Mail className="h-5 w-5 text-blue-500" />
                          <p className="text-blue-700 font-medium">Sending welcome email...</p>
                        </>
                      )}
                    </div>
                    <p className="text-blue-600 text-sm">
                      {welcomeEmailSent 
                        ? "Check your inbox for helpful getting started tips!"
                        : "Please wait while we send you helpful getting started tips..."
                      }
                    </p>
                  </div>
                )}

                <p className="text-gray-500 text-sm text-center">
                  Redirecting you to login in 3 seconds...
                </p>
                <div className="text-center">
                  <Link 
                    to="/login" 
                    state={{ message: "Email verified successfully! You can now log in." }}
                    className="inline-block bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Continue to Login
                  </Link>
                </div>
              </div>
            )}

            {/* Error State - UNCHANGED */}
            {status === "error" && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <p className="text-red-700 font-medium">Verification Failed</p>
                  </div>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
                <div className="text-center space-y-2">
                  <Link 
                    to="/login"
                    className="inline-block bg-gray-900 text-white px-6 py-2 rounded-md hover:bg-gray-800 transition-colors mr-2"
                  >
                    Back to Login
                  </Link>
                  <Link 
                    to="/signup"
                    className="inline-block text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Need to sign up again?
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default EmailVerifiedPage;
